import {
  FieldValues,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import {
  AutocompleteField,
  Button,
  GraphQLValidationErrors,
  HelperText,
  SelectField,
  SelectFieldOption,
  TextField,
  Title,
} from "@funcional/react-ui";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toDateTimeLocalFormat } from "@common/helpers";
import { Grid } from "@material-ui/core";
import { DiscountRuleRangesForm } from "./DiscountRuleRangesForm";
import { IDiscountRuleRange } from "@pbm/discount-rules/contracts";
import { v4 as uuid } from "uuid";
import { IGraphQLError } from "@common/contracts";

interface IDiscountRuleInput {
  type: string;
  monthlyLimit?: number;
  effectiveSince?: string;
  effectiveUntil?: string;
  accountedProductsEans: string[];
  discountedProductsEans: string[];
  ranges: any[];
}

const containerSpacing = 4;

const schema = yup.object().shape({
  monthlyLimit: yup
    .number()
    .min(1, "deve ser maior ou igual a 1")
    .integer("deve ser um número inteiro")
    .required("é um campo obrigatório")
    .nullable(),
  type: yup
    .string()
    .required("é um campo obrigatório")
    .nullable()
    .oneOf([null, "PROGRESSIVE", "COMBO"]),
  effectiveSince: yup.date().required("é um campo obrigatório").nullable(),
  effectiveUntil: yup
    .date()
    .min(
      yup.ref("effectiveSince"),
      "a data final não pode ser inferior à inicial"
    )
    .required("é um campo obrigatório")
    .nullable(),
  discountedProductsEans: yup
    .array()
    .of(yup.string().length(13, "Cada EAN deve conter 13 dígitos"))
    .min(1, "deve conter pelo menos um EAN"),
  accountedProductsEans: yup
    .array()
    .of(yup.string().length(13, "Cada EAN deve conter 13 dígitos"))
    .min(1, "deve conter pelo menos um EAN"),
  ranges: yup
    .array()
    .of(
      yup.object().shape({
        boxesFrom: yup
          .number()
          .integer("deve ser um número inteiro")
          .required("obrigatório")
          .nullable()
          .min(1),
        boxesTo: yup
          .number()
          .integer("deve ser um número inteiro")
          .nullable()
          .transform((value, originalValue) =>
            typeof originalValue === "string" && originalValue === ""
              ? null
              : value
          )
          .min(
            yup.ref("boxesFrom"),
            "a quantidade final deve ser igual ou maior a inicial"
          ),
        saleDiscountPercentage: yup
          .number()
          .required("obrigatório")
          .nullable()
          .min(0, "deve estar entre 0% e 100%")
          .max(100, "deve estar entre 0% e 100%"),
        daysBetweenSalesInterval: yup
          .number()
          .integer("deve ser um número inteiro")
          .required("obrigatório")
          .nullable()
          .min(0),
        daysWithoutSalesTolerance: yup
          .number()
          .integer("deve ser um número inteiro")
          .required("obrigatório")
          .nullable()
          .min(0),
      })
    )
    .min(1, "deve conter pelo menos uma faixa de desconto"),
});

export function DiscountRuleForm({
  onSubmit,
  defaults,
  isLoading,
  graphQLErrors,
}: {
  onSubmit: SubmitHandler<FieldValues>;
  defaults?: IDiscountRuleInput;
  isLoading?: boolean;
  graphQLErrors?: IGraphQLError[];
}) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...defaults,
      discountedProductsEans: defaults?.discountedProductsEans || [],
      accountedProductsEans: defaults?.accountedProductsEans || [],
      ranges: (defaults?.ranges || []) as IDiscountRuleRange[],
      effectiveSince: toDateTimeLocalFormat(defaults?.effectiveSince),
      effectiveUntil: toDateTimeLocalFormat(defaults?.effectiveUntil),
    } as any,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "ranges",
  });

  const addOption = () => {
    const previous: any = fields[fields.length - 1];
    append([
      {
        id: uuid(),
        boxesTo: "",
        boxesFrom: previous ? previous.boxesTo + 1 : "1",
        saleDiscountPercentage: previous?.salesDiscountPercentag || "",
        daysBetweenSalesInterval: previous?.daysBetweenSalesInterval || "",
        daysWithoutSalesTolerance: previous?.daysWithoutSalesTolerance || "",
      } as never,
    ]);
  };
  const deleteOption = (row: any) => {
    if (window.confirm("Tem certeza de que deseja excluir esta faixa?")) {
      remove(row.index);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="w-full max-w-2xl"
      noValidate
      autoComplete="off"
    >
      <GraphQLValidationErrors graphQLErrors={graphQLErrors} />

      <Grid container spacing={containerSpacing}>
        <Grid item xs={6}>
          <SelectField
            required
            errorMessage={errors?.type?.message}
            name="type"
            control={control}
            defaultValue={defaults?.type || ""}
            id="type"
            label="Tipo de desconto"
            helperText="Selecione o tipo de regra de desconto"
          >
            <SelectFieldOption value={undefined}>-</SelectFieldOption>
            <SelectFieldOption value="PROGRESSIVE">
              Progressivo
            </SelectFieldOption>
            <SelectFieldOption value="COMBO">Combo</SelectFieldOption>
          </SelectField>
        </Grid>

        <Grid item xs={6}>
          <TextField
            required
            control={control}
            type="number"
            id="monthlyLimit"
            label="Limite Mensal"
            helperText="Quantas unidades do produto podem ser compradas dentro de um mês"
            defaultValue={defaults?.monthlyLimit}
            errorMessage={errors?.monthlyLimit?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            required
            control={control}
            type="datetime-local"
            id="effectiveSince"
            label="Início da vigência"
            defaultValue={toDateTimeLocalFormat(defaults?.effectiveSince)}
            errorMessage={errors?.effectiveSince?.message}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            required
            control={control}
            type="datetime-local"
            id="effectiveUntil"
            label="Fim da vigência"
            defaultValue={toDateTimeLocalFormat(defaults?.effectiveUntil)}
            errorMessage={errors?.effectiveUntil?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <AutocompleteField
            freeSolo
            label="Produtos com Desconto"
            placeholder="Digite o EAN do produto e aperte enter"
            helperText="Estes são os produtos que receberão os descontos determinados abaixo"
            name="discountedProductsEans"
            control={control}
            options={(defaults?.discountedProductsEans || []).map((ean) => ({
              value: ean,
              label: ean,
            }))}
            defaultValue={(defaults?.discountedProductsEans || []).map(
              (ean) => ({
                label: ean,
                value: ean,
              })
            )}
            errorMessage={
              errors?.discountedProductsEans?.message ||
              ((errors?.discountedProductsEans?.length || 0) > 0
                ? errors?.discountedProductsEans
                    ?.filter((item: any) => item !== null)
                    .map((item: any) => item.message)
                : undefined)
            }
          />
        </Grid>

        <Grid item xs>
          <AutocompleteField
            freeSolo
            label="Produtos do Grupo"
            placeholder="Digite o EAN do produto e aperte enter"
            helperText="Estes são os produtos que contam no somatório de caixas vendidas para atingir os descontos"
            name="accountedProductsEans"
            control={control}
            options={(defaults?.accountedProductsEans || []).map((ean) => ({
              value: ean,
              label: ean,
            }))}
            defaultValue={defaults?.accountedProductsEans?.map((ean) => ({
              label: ean,
              value: ean,
            }))}
            errorMessage={errors?.accountedProductsEans?.message}
          />
        </Grid>

        <Grid item xs={12}>
          <Title level={3}>Faixas de desconto</Title>

          {errors?.ranges?.message ? (
            <HelperText errorMessage={errors?.ranges?.message} />
          ) : null}

          {fields ? (
            <DiscountRuleRangesForm
              register={register}
              data={fields as any}
              addOption={addOption}
              deleteOption={deleteOption}
              errors={errors}
            />
          ) : null}
        </Grid>

        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
